/**
 * Macronutrient Targets — V2 layout.
 *
 * Splits the legacy single-table card into:
 *   1. <TargetEnergyCard /> — input + slider for daily Target Energy (E).
 *   2. <MacroInputsCard /> — pie chart + legend at the top, then 4 per-macro
 *      cards (Protein, Carbs, Fat, Fibre), each with input, computed
 *      Reference RDA range, and an Energy Contribution bar.
 *
 * Both cards share state through <MacronutrientTargetsProvider>, so callers
 * can lay them out wherever (e.g. TargetEnergyCard sitting beside the
 * existing Weight/Energy Targets card in a 2-col row).
 *
 * Card 2 is gated on Card 1 being SAVED (not just edited) — the four
 * macro inputs only become editable once `target.recommended_calories`
 * is persisted with a value > 0.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Check, Loader2, Undo2 } from "lucide-react";

import {
  Widget,
  WidgetContent,
  WidgetHeader,
} from "@/components/consultation/shared/Widget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

import { useNutritionEvaluationContext } from "../NutritionEvaluationContext";
import {
  useHealthProfile,
  useNutritionTarget,
  useUpdateNutritionTarget,
} from "@/hooks/useNutritionEvaluation";
import { useDirtyFlag, useDirtyHighlight } from "@/hooks/useUnsavedChanges";
import type { NutritionTarget } from "@/types/nutrition";

import { NutrientPieChart } from "./nutrition-charts";
import {
  NUTRITION_COLORS,
  type NutrientPieSegment,
  type NutritionSegmentKey,
} from "./nutrition-chart-config";

// =============================================================================
// Constants & helpers
// =============================================================================

const ENERGY_MIN = 1200;
const ENERGY_MAX = 4500;
const ENERGY_STEP = 50;

const PROTEIN_KCAL_PER_G = 4;
const CARBS_KCAL_PER_G = 4;
const FAT_KCAL_PER_G = 9;
const FIBRE_KCAL_PER_G = 2;

// Per-macro hard upper bounds (g). Generous caps to catch typos like "9999"
// without constraining legitimate clinical decisions.
const PROTEIN_MAX_G = 500;
const CARBS_MAX_G = 1500;
const FAT_MAX_G = 500;
const FIBRE_MAX_G = 300;

// Diet energy is flagged (warning only, not enforced) when it deviates
// from Target Energy by more than this fraction.
const ENERGY_MISMATCH_TOLERANCE = 0.05;

const clampEnergy = (n: number) =>
  Math.min(
    ENERGY_MAX,
    Math.max(ENERGY_MIN, Math.round(n / ENERGY_STEP) * ENERGY_STEP)
  );

const toFloat = (s: string) => {
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

// Strip leading/embedded minus signs so paste-from-elsewhere ("-50") and
// keyboard "-" entries can't sneak past the native number input.
const stripNegative = (s: string) => s.replace(/-/g, "");

// Hide native number-input spinner arrows. Inline because the shared Input
// primitive shouldn't bake this in for everyone.
const NO_SPINNER =
  "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";

interface MacroDef {
  key: "protein" | "carbs" | "fat" | "fibre";
  /** Segment key in NUTRITION_CHART_CONFIG (American spelling for `fats`/`fiber`). */
  chartKey: NutritionSegmentKey;
  label: string;
  kcalPerG: number;
  maxG: number;
}

const MACROS: readonly MacroDef[] = [
  {
    key: "protein",
    chartKey: "protein",
    label: "Protein",
    kcalPerG: PROTEIN_KCAL_PER_G,
    maxG: PROTEIN_MAX_G,
  },
  {
    key: "carbs",
    chartKey: "carbs",
    label: "Carbs",
    kcalPerG: CARBS_KCAL_PER_G,
    maxG: CARBS_MAX_G,
  },
  {
    key: "fat",
    chartKey: "fats",
    label: "Fat",
    kcalPerG: FAT_KCAL_PER_G,
    maxG: FAT_MAX_G,
  },
  {
    key: "fibre",
    chartKey: "fiber",
    label: "Fibre",
    kcalPerG: FIBRE_KCAL_PER_G,
    maxG: FIBRE_MAX_G,
  },
] as const;

// =============================================================================
// Shared context
// =============================================================================

type MacroNumberMap = {
  protein: number;
  carbs: number;
  fat: number;
  fibre: number;
};
type MacroStringMap = {
  protein: string;
  carbs: string;
  fat: string;
  fibre: string;
};

interface MacroContextValue {
  isLoading: boolean;
  isCompleted: boolean;

  // Energy section
  energyRDA: string;
  setEnergyRDA: (v: string) => void;
  isEnergyDirty: boolean;
  isEnergyValid: boolean;
  saveTargetEnergy: () => void;
  revertEnergyRDA: () => void;

  // Macros section
  proteinGrams: string;
  carbsGrams: string;
  fatGrams: string;
  fibreGrams: string;
  setProteinGrams: (v: string) => void;
  setCarbsGrams: (v: string) => void;
  setFatGrams: (v: string) => void;
  setFibreGrams: (v: string) => void;
  isMacrosDirty: boolean;
  /** Per-field dirty flags so each macro tile can show its own undo button. */
  macroFieldDirty: {
    protein: boolean;
    carbs: boolean;
    fat: boolean;
    fibre: boolean;
  };
  revertMacroField: (key: "protein" | "carbs" | "fat" | "fibre") => void;
  carbsError: boolean;
  /** Per-macro "exceeds hard cap" flags. Blocks save when any are true. */
  macroMaxError: {
    protein: boolean;
    carbs: boolean;
    fat: boolean;
    fibre: boolean;
  };
  hasMacroMaxError: boolean;
  /**
   * Diet energy − Target Energy, only when both are set AND the deviation
   * exceeds ENERGY_MISMATCH_TOLERANCE. 0 means "no warning". Warning only —
   * does not block save.
   */
  energyMismatchKcal: number;
  macrosUnlocked: boolean;
  saveMacros: () => void;

  // Derived values consumed by both cards
  macroValues: MacroNumberMap;
  macroKcal: MacroNumberMap;
  macroPct: MacroNumberMap;
  referenceRDA: MacroStringMap;
  dietEnergy: number;

  // Common
  isSaving: boolean;
}

const MacroContext = createContext<MacroContextValue | null>(null);

const useMacroContext = () => {
  const ctx = useContext(MacroContext);
  if (!ctx) {
    throw new Error(
      "MacronutrientTargets components must be rendered inside <MacronutrientTargetsProvider>"
    );
  }
  return ctx;
};

// =============================================================================
// Provider — owns all shared state, fetches, and save handlers
// =============================================================================

export const MacronutrientTargetsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { appointmentId, isCompleted } = useNutritionEvaluationContext();
  const { data: healthProfile } = useHealthProfile(appointmentId);
  const { data: target, isLoading } = useNutritionTarget(appointmentId);
  const updateTarget = useUpdateNutritionTarget(appointmentId);

  const [proteinGrams, setProteinGrams] = useState("");
  const [carbsGrams, setCarbsGrams] = useState("");
  const [fatGrams, setFatGrams] = useState("");
  const [fibreGrams, setFibreGrams] = useState("");
  const [energyRDA, setEnergyRDA] = useState("");

  const syncRef = useRef(false);
  // Gate one-shot hydration on first query resolve, not on target truthiness — sibling Target Weight card creating the target row would otherwise re-fire and wipe drafts here.
  useEffect(() => {
    if (isLoading || syncRef.current) return;
    if (target) {
      setProteinGrams(target.protein != null ? target.protein.toString() : "");
      setCarbsGrams(target.carbs != null ? target.carbs.toString() : "");
      setFatGrams(target.fat != null ? target.fat.toString() : "");
      setFibreGrams(target.fibre != null ? target.fibre.toString() : "");
      setEnergyRDA(
        target.recommended_calories != null
          ? target.recommended_calories.toString()
          : ""
      );
    }
    syncRef.current = true;
  }, [isLoading, target]);

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------

  const macroValues = useMemo<MacroNumberMap>(
    () => ({
      protein: toFloat(proteinGrams),
      carbs: toFloat(carbsGrams),
      fat: toFloat(fatGrams),
      fibre: toFloat(fibreGrams),
    }),
    [proteinGrams, carbsGrams, fatGrams, fibreGrams]
  );

  const macroKcal = useMemo<MacroNumberMap>(() => {
    // Carbs are counted net of fibre to avoid double-counting energy.
    const netCarbs = Math.max(0, macroValues.carbs - macroValues.fibre);
    return {
      protein: macroValues.protein * PROTEIN_KCAL_PER_G,
      carbs: netCarbs * CARBS_KCAL_PER_G,
      fat: macroValues.fat * FAT_KCAL_PER_G,
      fibre: macroValues.fibre * FIBRE_KCAL_PER_G,
    };
  }, [macroValues]);

  const dietEnergy = useMemo(
    () =>
      Math.round(
        macroKcal.protein + macroKcal.carbs + macroKcal.fat + macroKcal.fibre
      ),
    [macroKcal]
  );

  const macroPct = useMemo<MacroNumberMap>(() => {
    if (dietEnergy === 0) return { protein: 0, carbs: 0, fat: 0, fibre: 0 };
    return {
      protein: Math.round((macroKcal.protein * 100) / dietEnergy),
      carbs: Math.round((macroKcal.carbs * 100) / dietEnergy),
      fat: Math.round((macroKcal.fat * 100) / dietEnergy),
      fibre: Math.round((macroKcal.fibre * 100) / dietEnergy),
    };
  }, [dietEnergy, macroKcal]);

  const energyRDANum = toFloat(energyRDA);
  const weightKg = healthProfile?.weight_in_kgs ?? 0;

  const referenceRDA = useMemo<MacroStringMap>(() => {
    if (energyRDANum <= 0) {
      return { protein: "—", carbs: "—", fat: "—", fibre: "—" };
    }
    const proteinLow = Math.round(0.83 * weightKg);
    const proteinHigh = Math.round((0.15 * energyRDANum) / 4);
    const carbsLow = Math.round((0.45 * energyRDANum) / 4);
    const carbsHigh = Math.round((0.65 * energyRDANum) / 4);
    const fatLow = Math.round((0.15 * energyRDANum) / 9);
    const fatHigh = Math.round((0.35 * energyRDANum) / 9);
    const fibreVal = Math.round((15 * energyRDANum) / 1000);
    return {
      protein: `${proteinLow} – ${proteinHigh} g`,
      carbs: `${carbsLow} – ${carbsHigh} g`,
      fat: `${fatLow} – ${fatHigh} g`,
      fibre: `${fibreVal} g`,
    };
  }, [energyRDANum, weightKg]);

  // Carbs must cover at least the fibre amount, otherwise net-carb energy
  // would be negative.
  const carbsError =
    macroValues.carbs > 0 && macroValues.carbs < macroValues.fibre;

  const macroMaxError = useMemo(
    () => ({
      protein: macroValues.protein > PROTEIN_MAX_G,
      carbs: macroValues.carbs > CARBS_MAX_G,
      fat: macroValues.fat > FAT_MAX_G,
      fibre: macroValues.fibre > FIBRE_MAX_G,
    }),
    [macroValues]
  );
  const hasMacroMaxError =
    macroMaxError.protein ||
    macroMaxError.carbs ||
    macroMaxError.fat ||
    macroMaxError.fibre;

  // Warning only — flags when macro-derived diet energy drifts more than
  // ENERGY_MISMATCH_TOLERANCE from the saved Target Energy.
  const energyMismatchKcal = useMemo(() => {
    if (energyRDANum <= 0 || dietEnergy <= 0) return 0;
    const diff = dietEnergy - energyRDANum;
    return Math.abs(diff) / energyRDANum >= ENERGY_MISMATCH_TOLERANCE
      ? diff
      : 0;
  }, [energyRDANum, dietEnergy]);

  // ---------------------------------------------------------------------------
  // Dirty flags & save handlers (one per card)
  // ---------------------------------------------------------------------------

  const baselineEnergyRDA =
    target?.recommended_calories != null
      ? target.recommended_calories.toString()
      : "";
  const isEnergyDirty = !isCompleted && energyRDA !== baselineEnergyRDA;
  useDirtyFlag(
    `target-energy:${appointmentId}`,
    isEnergyDirty,
    "Target Energy"
  );

  const baselineMacros = useMemo<MacroStringMap>(
    () => ({
      protein: target?.protein != null ? target.protein.toString() : "",
      carbs: target?.carbs != null ? target.carbs.toString() : "",
      fat: target?.fat != null ? target.fat.toString() : "",
      fibre: target?.fibre != null ? target.fibre.toString() : "",
    }),
    [target]
  );

  const macroFieldDirty = useMemo(
    () => ({
      protein: !isCompleted && proteinGrams !== baselineMacros.protein,
      carbs: !isCompleted && carbsGrams !== baselineMacros.carbs,
      fat: !isCompleted && fatGrams !== baselineMacros.fat,
      fibre: !isCompleted && fibreGrams !== baselineMacros.fibre,
    }),
    [
      isCompleted,
      proteinGrams,
      carbsGrams,
      fatGrams,
      fibreGrams,
      baselineMacros,
    ]
  );
  const isMacrosDirty =
    macroFieldDirty.protein ||
    macroFieldDirty.carbs ||
    macroFieldDirty.fat ||
    macroFieldDirty.fibre;
  useDirtyFlag(`macro-targets:${appointmentId}`, isMacrosDirty, "Macros");

  const revertEnergyRDA = useCallback(
    () => setEnergyRDA(baselineEnergyRDA),
    [baselineEnergyRDA]
  );
  const revertMacroField = useCallback(
    (key: "protein" | "carbs" | "fat" | "fibre") => {
      const setters = {
        protein: setProteinGrams,
        carbs: setCarbsGrams,
        fat: setFatGrams,
        fibre: setFibreGrams,
      } as const;
      setters[key](baselineMacros[key]);
    },
    [baselineMacros]
  );

  const isEnergyValid =
    energyRDANum >= ENERGY_MIN && energyRDANum <= ENERGY_MAX;

  const saveTargetEnergy = useCallback(() => {
    if (!isEnergyDirty || !isEnergyValid) return;
    const data: Partial<NutritionTarget> = {
      ...target,
      recommended_calories: energyRDANum,
    };
    delete data.id;
    updateTarget.mutate({ data, isNew: !target });
  }, [isEnergyDirty, isEnergyValid, energyRDANum, target, updateTarget]);

  const saveMacros = useCallback(() => {
    if (!isMacrosDirty || carbsError || hasMacroMaxError) return;
    const data: Partial<NutritionTarget> = {
      ...target,
      protein: macroValues.protein || undefined,
      carbs: macroValues.carbs || undefined,
      fat: macroValues.fat || undefined,
      fibre: macroValues.fibre || undefined,
      energy: dietEnergy || undefined,
    };
    delete data.id;
    updateTarget.mutate({ data, isNew: !target });
  }, [
    isMacrosDirty,
    carbsError,
    hasMacroMaxError,
    macroValues,
    dietEnergy,
    target,
    updateTarget,
  ]);

  // Macros card unlocks only after Target Energy is PERSISTED — not just typed.
  const macrosUnlocked = (target?.recommended_calories ?? 0) > 0;

  const value: MacroContextValue = {
    isLoading,
    isCompleted,
    energyRDA,
    setEnergyRDA,
    isEnergyDirty,
    isEnergyValid,
    saveTargetEnergy,
    revertEnergyRDA,
    proteinGrams,
    carbsGrams,
    fatGrams,
    fibreGrams,
    setProteinGrams,
    setCarbsGrams,
    setFatGrams,
    setFibreGrams,
    isMacrosDirty,
    macroFieldDirty,
    revertMacroField,
    carbsError,
    macroMaxError,
    hasMacroMaxError,
    energyMismatchKcal,
    macrosUnlocked,
    saveMacros,
    macroValues,
    macroKcal,
    macroPct,
    referenceRDA,
    dietEnergy,
    isSaving: updateTarget.isPending,
  };

  return (
    <MacroContext.Provider value={value}>{children}</MacroContext.Provider>
  );
};

// =============================================================================
// Loading skeleton — used by both cards
// =============================================================================

const LoadingWidget: React.FC = () => (
  <Widget>
    <WidgetContent className="flex items-center justify-center py-8">
      <Loader2 className="size-5 animate-spin text-muted-foreground" />
    </WidgetContent>
  </Widget>
);

// =============================================================================
// TargetEnergyCard — input + slider only (pie moved to MacroInputsCard)
// =============================================================================

export const TargetEnergyCard: React.FC = () => {
  const {
    isLoading,
    isCompleted,
    energyRDA,
    setEnergyRDA,
    isEnergyDirty,
    isEnergyValid,
    isSaving,
    saveTargetEnergy,
    revertEnergyRDA,
  } = useMacroContext();
  const highlighted = useDirtyHighlight(isEnergyDirty);

  if (isLoading) return <LoadingWidget />;

  const numeric = toFloat(energyRDA);
  const sliderValue = numeric > 0 ? clampEnergy(numeric) : ENERGY_MIN;

  return (
    <Widget>
      <WidgetHeader title="Target Energy" hasInfo />
      <WidgetContent>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="z-eyebrow text-muted-foreground">
              Daily Target
              <span className="text-destructive ml-0.5">*</span>
            </Label>
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  "rounded-3xl transition-shadow duration-300",
                  highlighted &&
                    "ring-4 ring-warning/50 ring-offset-2 ring-offset-background"
                )}
              >
                <div className="relative w-40">
                  <Input
                    type="number"
                    step={ENERGY_STEP}
                    min={ENERGY_MIN}
                    max={ENERGY_MAX}
                    value={energyRDA}
                    onChange={(e) =>
                      setEnergyRDA(stripNegative(e.target.value))
                    }
                    disabled={isCompleted}
                    placeholder={`${ENERGY_MIN}`}
                    aria-invalid={!isEnergyValid && energyRDA !== ""}
                    className={cn(
                      "pr-12 z-numeric",
                      NO_SPINNER,
                      isEnergyDirty &&
                        "border-warning/60 bg-warning/10 focus-visible:border-warning focus-visible:ring-warning/30"
                    )}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 z-caption text-muted-foreground pointer-events-none">
                    kcal
                  </span>
                </div>
              </div>
              {isEnergyDirty && !isCompleted && (
                <>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={revertEnergyRDA}
                    disabled={isSaving}
                    className="size-9 rounded-full text-muted-foreground hover:text-foreground"
                    aria-label="Discard target energy edit"
                  >
                    <Undo2 className="size-4" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    onClick={saveTargetEnergy}
                    disabled={!isEnergyValid || isSaving}
                    className="size-9 rounded-full"
                    aria-label="Save target energy"
                  >
                    {isSaving ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Check className="size-4" />
                    )}
                  </Button>
                </>
              )}
            </div>
            {energyRDA !== "" && !isEnergyValid && (
              <p className="z-hint text-destructive">
                Enter a value between {ENERGY_MIN} and {ENERGY_MAX} kcal.
              </p>
            )}
          </div>

          <div className="w-80 max-w-full space-y-1.5">
            <Slider
              value={[sliderValue]}
              min={ENERGY_MIN}
              max={ENERGY_MAX}
              step={ENERGY_STEP}
              onValueChange={(v) => setEnergyRDA(String(v[0] ?? ENERGY_MIN))}
              disabled={isCompleted}
              aria-label="Target energy slider"
            />
            <div className="flex justify-between z-hint text-muted-foreground">
              <span>{ENERGY_MIN} kcal</span>
              <span>{ENERGY_MAX} kcal</span>
            </div>
          </div>
        </div>
      </WidgetContent>
    </Widget>
  );
};

// =============================================================================
// MacroInputsCard — pie + legend at the top, then 4 macro tiles
// =============================================================================

export const MacroInputsCard: React.FC = () => {
  const {
    isLoading,
    isCompleted,
    macroKcal,
    macroPct,
    referenceRDA,
    carbsError,
    macroMaxError,
    hasMacroMaxError,
    energyMismatchKcal,
    macrosUnlocked,
    isMacrosDirty,
    macroFieldDirty,
    revertMacroField,
    isSaving,
    proteinGrams,
    carbsGrams,
    fatGrams,
    fibreGrams,
    setProteinGrams,
    setCarbsGrams,
    setFatGrams,
    setFibreGrams,
    dietEnergy,
    energyRDA,
    saveMacros,
  } = useMacroContext();

  if (isLoading) return <LoadingWidget />;

  const cardValues = {
    protein: { value: proteinGrams, onChange: setProteinGrams },
    carbs: { value: carbsGrams, onChange: setCarbsGrams },
    fat: { value: fatGrams, onChange: setFatGrams },
    fibre: { value: fibreGrams, onChange: setFibreGrams },
  };

  const inputsDisabled = isCompleted || !macrosUnlocked;

  const segments: NutrientPieSegment[] = [
    { key: "protein", value: macroKcal.protein },
    { key: "carbs", value: macroKcal.carbs },
    { key: "fats", value: macroKcal.fat },
    { key: "fiber", value: macroKcal.fibre },
  ];

  const legendRows = MACROS.map((m) => ({
    label: m.label,
    color: NUTRITION_COLORS[m.chartKey],
    pct: macroPct[m.key],
    kcal: macroKcal[m.key],
  }));

  return (
    <Widget>
      <WidgetHeader title="Set Macros" hasInfo />
      <WidgetContent>
        {/* Pie + legend header — split on md+, stacked on mobile */}
        <div className="flex flex-col items-center gap-6 pb-5 mb-5 border-b border-border md:flex-row md:items-center md:justify-around md:gap-10">
          <NutrientPieChart
            title="Diet Energy"
            value={dietEnergy}
            segments={segments}
          />
          <ul className="grid grid-cols-2 gap-x-8 gap-y-2 min-w-0">
            {legendRows.map((row) => (
              <li key={row.label} className="flex items-center gap-3 min-w-0">
                <span
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: row.color }}
                  aria-hidden
                />
                <span className="z-body text-foreground truncate">
                  {row.label}
                </span>
                <span className="ml-auto flex items-baseline gap-1">
                  <span className="z-body z-numeric text-foreground">
                    {row.pct}%
                  </span>
                  <span className="z-hint text-muted-foreground">
                    ({Math.round(row.kcal)} kcal)
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        {!macrosUnlocked && !isCompleted && (
          <div className="mb-4 rounded-md border border-border bg-muted/40 px-3 py-2">
            <p className="z-hint text-muted-foreground">
              Set and save the Target Energy above to enable macro inputs.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {MACROS.map((m) => (
            <MacroCard
              key={m.key}
              label={m.label}
              value={cardValues[m.key].value}
              onChange={cardValues[m.key].onChange}
              kcal={macroKcal[m.key]}
              kcalPerG={m.kcalPerG}
              maxG={m.maxG}
              pct={macroPct[m.key]}
              referenceRDA={referenceRDA[m.key]}
              color={NUTRITION_COLORS[m.chartKey]}
              disabled={inputsDisabled}
              hasError={
                (m.key === "carbs" && carbsError) || macroMaxError[m.key]
              }
              isDirty={macroFieldDirty[m.key]}
              onRevert={() => revertMacroField(m.key)}
            />
          ))}
        </div>

        {(carbsError || hasMacroMaxError) && (
          <div className="space-y-1 pt-3">
            {carbsError && (
              <p className="z-hint text-destructive">
                Carbs must be at least the fibre amount.
              </p>
            )}
            {macroMaxError.protein && (
              <p className="z-hint text-destructive">
                Protein cannot exceed {PROTEIN_MAX_G} g.
              </p>
            )}
            {macroMaxError.carbs && (
              <p className="z-hint text-destructive">
                Carbs cannot exceed {CARBS_MAX_G} g.
              </p>
            )}
            {macroMaxError.fat && (
              <p className="z-hint text-destructive">
                Fat cannot exceed {FAT_MAX_G} g.
              </p>
            )}
            {macroMaxError.fibre && (
              <p className="z-hint text-destructive">
                Fibre cannot exceed {FIBRE_MAX_G} g.
              </p>
            )}
          </div>
        )}

        {energyMismatchKcal !== 0 && (
          <p className="z-hint text-warning pt-2">
            Macro total ({dietEnergy} kcal) is{" "}
            {Math.abs(Math.round(energyMismatchKcal))} kcal{" "}
            {energyMismatchKcal > 0 ? "over" : "under"} Target Energy (
            {toFloat(energyRDA)} kcal).
          </p>
        )}

        {!isCompleted && (
          <div className="flex items-center justify-end gap-3 pt-5">
            {isMacrosDirty && (
              <span className="z-hint text-muted-foreground">
                Unsaved changes
              </span>
            )}
            <Button
              size="sm"
              onClick={saveMacros}
              disabled={
                !macrosUnlocked ||
                !isMacrosDirty ||
                carbsError ||
                hasMacroMaxError ||
                isSaving
              }
            >
              {isSaving && <Loader2 className="size-4 animate-spin" />}
              Save Macros
            </Button>
          </div>
        )}
      </WidgetContent>
    </Widget>
  );
};

// =============================================================================
// MacroCard — single macro tile (input + reference + %E bar)
// =============================================================================

interface MacroCardProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  kcal: number;
  kcalPerG: number;
  maxG: number;
  pct: number;
  referenceRDA: string;
  color: string;
  disabled?: boolean;
  hasError?: boolean;
  isDirty?: boolean;
  onRevert?: () => void;
}

const MacroCard: React.FC<MacroCardProps> = ({
  label,
  value,
  onChange,
  kcal,
  kcalPerG,
  maxG,
  pct,
  referenceRDA,
  color,
  disabled,
  hasError,
  isDirty = false,
  onRevert,
}) => {
  const barWidth = Math.max(0, Math.min(100, pct));
  const highlighted = useDirtyHighlight(isDirty);

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-lg border border-border bg-muted/30 p-4 transition-shadow duration-300",
        hasError && "border-destructive/50 bg-destructive/5",
        disabled && "opacity-60",
        highlighted &&
          "ring-4 ring-warning/50 ring-offset-2 ring-offset-background"
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="z-h3 text-foreground">{label}</span>
        <span
          className="size-2 rounded-full"
          style={{ backgroundColor: color }}
          aria-hidden
        />
      </div>

      <p className="z-secondary text-muted-foreground">
        Reference RDA: {referenceRDA}
      </p>

      <div className="space-y-1.5">
        <Label className="z-eyebrow text-muted-foreground">
          Set Target (g)
        </Label>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Input
              type="number"
              min={0}
              max={maxG}
              step={0.1}
              value={value}
              onChange={(e) => onChange(stripNegative(e.target.value))}
              disabled={disabled}
              placeholder="0"
              aria-invalid={hasError || undefined}
              className={cn(
                "pr-9 z-numeric",
                NO_SPINNER,
                isDirty &&
                  !hasError &&
                  "border-warning/60 bg-warning/10 focus-visible:border-warning focus-visible:ring-warning/30",
                hasError &&
                  "border-destructive/60 focus-visible:border-destructive"
              )}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 z-caption text-muted-foreground pointer-events-none">
              g
            </span>
          </div>
          {isDirty && !disabled && onRevert && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={onRevert}
              className="size-9 rounded-full text-muted-foreground hover:text-foreground"
              aria-label={`Discard ${label} edit`}
            >
              <Undo2 className="size-4" />
            </Button>
          )}
        </div>
        <p className="z-hint text-muted-foreground">
          {value
            ? `${toFloat(value)} × ${kcalPerG} = ${Math.round(kcal)} kcal`
            : "—"}
        </p>
      </div>

      <div className="space-y-1.5 mt-auto">
        <div className="flex items-center justify-between">
          <span className="z-eyebrow text-muted-foreground">
            Energy Contribution
          </span>
          <span className="z-caption z-numeric text-foreground">{pct}%</span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${barWidth}%`, backgroundColor: color }}
          />
        </div>
      </div>
    </div>
  );
};
